import "./QuestionResultsGraph.scss";

import { Avatar, Flex, FlexItem, List, Text } from "@fluentui/react-northstar";
import {
  NominalQuestion,
  OpenEndedQuestion,
  Question,
  QuestionChoice,
  QuestionResponse,
  QuestionType,
  TeamsAnonymityLevel,
  TeamsSurveyResponse,
} from "@teams/types";
import moment from "moment";
import React, { Fragment, ReactNode, useMemo } from "react";

import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { useUserResponseValue } from "../hooks/useUserResponeValue";
import { ChoiceQuestion } from "./QuestionVoteField";
import { ACLRole } from "@teams/types/api";
import { useUserInfo } from "../../../../_shared/actions/useUserInfo";

type Props<T extends Question<QuestionType> | ChoiceQuestion> = {
  anononymity: TeamsAnonymityLevel;
  role: ACLRole | undefined,
  question: T;
  canViewResults: boolean;
  responses: TeamsSurveyResponse[];
  userResponse: TeamsSurveyResponse | undefined;
  maxRespondents?: number;
  hideAvatars?: boolean;
  darkBackground?: boolean;
};

export const QuestionResultsGraph: React.FC<Props<Question<QuestionType>>> = props => {
  
  const { question, userResponse, canViewResults, responses, role } = props;
  const userResponseValue = useUserResponseValue(question, userResponse);

  const userQuestionResponse = useMemo(
    () => {
      if (userResponseValue) {
        return (
          <Flex gap="gap.small" vAlign="start">
            <Text content="You voted: " className="votes-summary-text" />
            <>{userResponseValue}</>
          </Flex>
        );
      }

      return null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userResponseValue]
  );

  if (!canViewResults) {
    return userQuestionResponse;
  }

  return (
    <Flex column gap="gap.small" className="question-results-graph-container">
      {(question.questionType === "ordinal" || question.questionType === "nominal") && (
        <ChoiceQuestionGraph {...(props as Props<NominalQuestion>)} />
      )}

      {question.questionType === "open-ended" && (
        <OpenEndedResultsList {...(props as Props<OpenEndedQuestion>)} />
      )}

      {userQuestionResponse && (
        <Flex column hAlign="start">
          { role === "own" && (<Text content={`Total Votes: ${responses.length}`} className="votes-summary-text" />)}
          {userQuestionResponse}
        </Flex>
      )}
    </Flex>
  );
};

const OpenEndedResultsList: React.FC<Props<OpenEndedQuestion>> = ({
  question,
  responses,
  darkBackground,
}) => {
  return (
    <Flex
      column
      gap="gap.small"
      className={`question-results-graph ${darkBackground ? "dark" : ""}`}
    >
      <div className="question-results-graph__open-ended">
        <List
          items={responses.map(response => {
            return {
              key: response.id,
              className: "question-results-graph__open-ended-item",
              header: (
                <Flex gap="gap.smaller" vAlign="center" style={{ marginBottom: ".33rem" }}>
                  <Avatar name={response.user.name} size="smallest" />
                  <Text content={response.user.name} weight="bold" />
                </Flex>
              ),
              content: formatOpenEndedValue(
                question,
                response.responses.find(q => q.questionId === question.id)
              ),
            };
          })}
        />
      </div>
      <div className="question-results-graph__divider" />
      <Text content={`Total: ${responses.length}`} weight="bold" />
    </Flex>
  );
};

const ChoiceQuestionGraph: React.FC<Props<ChoiceQuestion>> = ({
  question,
  role,
  responses,
  anononymity,
  hideAvatars,
  darkBackground,
}) => {

  const { user } = useUserInfo();

  const value = useMemo(() => {
    return question.choices.map<{
      value: string;
      id: string;
      votes: TeamsSurveyResponse[];
      percent: number;
      correct?: boolean;
    }>(choice => {
      const v = {
        value: choice.label || (choice.value as string),
        id: choice.id,
        votes: responses.filter(r =>
          r.responses.some(rr => rr.questionId === question.id && rr.value.includes(choice.id))
        ),
        percent: 0,
        correct: choice.correct,
      };

      if (v.votes.length > 0) {
        v.percent = Math.round((v.votes.length / responses.length) * 100);
      }

      return v;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, responses.length]);

  return (
    <Flex
      column
      gap="gap.small"
      className={`question-results-graph ${darkBackground ? "dark" : ""}`}
    >
      <table>
        <tbody>
          {value.map((choice, index) => {
            return (
              <Fragment key={choice.id}>
                <tr>
                  <td style={{ width: "100%", paddingTop: index > 0 ? "1rem" : undefined }}>
                    <Flex column fill>
                      <FlexItem
                        styles={{ paddingBottom: "3px", maxWidth: "350px", wordWrap: "break-word" }}
                      >
                        <Text content={getChoiceQuestionLabel(choice)} weight="bold" />
                      </FlexItem>
                      <Flex fill vAlign="center" gap="gap.small">
                        <Flex className="question-results-graph__bar">
                          <span
                            className={`question-results-graph__bar-fill ${
                              choice.correct === false
                                ? "question-results-graph__bar-fill-wrong"
                                : ""
                            }`}
                            style={{ width: `${  role === "own" ? choice.percent : ( choice.votes.find(vote=>vote.user.aadObjectId === user.userId) ? 100 : 0 )}%` }}
                          />
                        </Flex>
                        { role === "own" && ( 
                          <FlexItem push>
                          <Text
                            style={{ whiteSpace: "nowrap" }}
                            content={`${choice.percent}% (${choice.votes.length.toString()})`}
                          />
                        </FlexItem>)
                        }
                      </Flex>
                    </Flex>
                  </td>
                </tr>
                {!hideAvatars && anononymity === "none"  && role === "own" && (
                  <tr>
                    <td>
                      <UserAvatarsList responses={choice.votes} max={7} />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </Flex>
  );
};

const UserAvatarsList: React.FC<{ responses: TeamsSurveyResponse[]; max: number }> = ({
  responses,
  max,
}) => {
  const firstMaxItems = useMemo(() => {
    return responses.slice(0, max).map(r => ({ name: r.user.name || r.user.upn, id: r.id }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responses.length]);

  return (
    <Flex gap="gap.smaller" wrap vAlign="center">
      {firstMaxItems.map(user => (
        <Avatar key={user.id} name={user.name} size="smallest" />
      ))}
      {responses.length > max && (
        <Text
          size="small"
          weight="light"
          style={{ whiteSpace: "nowrap" }}
          content={`${responses.length - max} more...`}
        />
      )}
    </Flex>
  );
};

function getChoiceQuestionLabel(choice: QuestionChoice<string | number>): ReactNode {
  if (choice.correct === undefined) {
    return <>{choice.value}</>;
  }
  return (
    <Flex vAlign="start">
      <PollyIcon
        icon={choice.correct ? "correct-icon" : "incorrect-icon"}
        style={{ marginRight: "3px", marginTop: "4px" }}
        className={
          choice.correct
            ? "question-results-graph__choice-correct"
            : "question-results-graph__choice-incorrect"
        }
        inheritColor
      />
      <Text content={choice.value} />
    </Flex>
  );
}

function formatOpenEndedValue(question: OpenEndedQuestion, response: QuestionResponse | undefined) {
  if (!response) {
    return null;
  }

  switch (question.contentType) {
    case "text/date":
      return response.value ? moment(response.value).format("l") : "";
    default:
      return response.value as string;
  }
}
